import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "sonner";

/**
 * Error shape understood by `useNotifiedMutation`. `__suppressToast` lets
 * callers opt a specific thrown error out of the default failure toast.
 */
export interface AppError {
  message?: string;
  __suppressToast?: boolean;
}

type MessageOrFn<TData, TVariables> =
  | string
  | ((data: TData, variables: TVariables) => string);

type ErrorMessageOrFn<TError, TVariables> =
  | string
  | ((error: TError, variables: TVariables) => string);

interface NotifiedMutationOptions<TData, TError, TVariables, TContext>
  extends UseMutationOptions<TData, TError, TVariables, TContext> {
  /** Toast shown when the mutation succeeds. */
  successMessage?: MessageOrFn<TData, TVariables>;
  /**
   * Toast shown when the mutation fails. If omitted, falls back to
   * the error's `message` property, then a generic fallback.
   */
  errorMessage?: ErrorMessageOrFn<TError, TVariables>;
}

/**
 * Wrapper around `useMutation` that emits sonner toasts on success/error
 * while preserving any caller-supplied `onSuccess` / `onError` callbacks.
 *
 * Pass `successMessage: undefined` to opt out of the success toast (e.g.
 * for background telemetry calls). Errors always notify unless the thrown
 * error has `__suppressToast: true`.
 */
export function useNotifiedMutation<
  TData = unknown,
  TError = AppError,
  TVariables = void,
  TContext = unknown,
>(
  options: NotifiedMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { successMessage, errorMessage, onSuccess, onError, ...rest } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (successMessage !== undefined) {
        const msg =
          typeof successMessage === "function"
            ? successMessage(data, variables)
            : successMessage;
        if (msg) toast.success(msg);
      }
      onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      const appError = error as AppError | undefined;
      if (!appError?.__suppressToast) {
        const msg =
          typeof errorMessage === "function"
            ? errorMessage(error, variables)
            : errorMessage ??
              appError?.message ??
              "An unexpected error occurred.";
        if (msg) toast.error(msg, { duration: 8000 });
      }
      onError?.(error, variables, onMutateResult, context);
    },
  });
}
